import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { LoadManifestService } from '../../../modules/wizard/services/laod-manifest.service';
import { JobFileService } from '@core/services/job-file.service';
import { Store } from '@ngxs/store';
import { JobFile } from '@core/models/job-file/job-file.model';
import { LoadManagementState } from '../../../modules/load-management/store/load-management.state';
import { WizardV2State } from '../../../modules/wizard-v2/store/wizard.state';
import { catchError, tap } from 'rxjs/operators';
import { of } from 'rxjs';

@Component({
  selector: 'hmt-documents-dialog',
  templateUrl: './documents-dialog.component.html',
  styleUrls: ['./documents-dialog.component.scss'],
  standalone: false
})
export class DocumentsDialogComponent implements OnInit {
  documents = [
    { name: 'Packing List', hasDownload: true },
    { name: 'Load Manifest', hasDownload: false },
    { name: 'Consolidated Load Manifest', hasDownload: false }
  ];

  constructor(
    public dialogRef: MatDialogRef<DocumentsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { orgId: string; jobRefId: string },
    private loadManifestService: LoadManifestService,
    private jobFileService: JobFileService,
    private store: Store
  ) {}

  ngOnInit(): void {
    this.checkPlannedDeliveryDates();
  }

  private checkPlannedDeliveryDates(): void {
    if (!this.data?.orgId || !this.data?.jobRefId) {
      console.error('Missing orgId or jobRefId');
      return;
    }

    // Try to get jobfile from LoadManagementState first (for load-management page)
    let jobFile: JobFile | null = this.store.selectSnapshot(LoadManagementState.jobFile);
    
    // If not found, try WizardV2State (for job-planning page)
    if (!jobFile) {
      jobFile = this.store.selectSnapshot(WizardV2State.getJobFile);
    }

    // If still not found, fallback to network request
    if (!jobFile) {
      console.warn('Jobfile not found in state, falling back to network request');
      this.jobFileService.getJobFileByOrgIdAndJobRefId(this.data.orgId, this.data.jobRefId)
        .subscribe({
          next: (jobFile: JobFile) => {
            this.updateDocumentsBasedOnJobFile(jobFile);
          },
          error: (error) => {
            console.error('Error fetching jobfile:', error);
            // Keep default state (Packing List visible, others hidden)
          }
        });
      return;
    }

    // Use jobfile from state
    this.updateDocumentsBasedOnJobFile(jobFile);
  }

  private updateDocumentsBasedOnJobFile(jobFile: JobFile): void {
    const allServicesHaveDates = this.allServicesHavePlannedDeliveryDates(jobFile);
    
    // Update download icons for Load Manifest and Consolidated Load Manifest
    this.documents = this.documents.map(doc => {
      if (doc.name === 'Load Manifest' || doc.name === 'Consolidated Load Manifest') {
        return { ...doc, hasDownload: allServicesHaveDates };
      }
      return doc;
    });
  }

  private allServicesHavePlannedDeliveryDates(jobFile: JobFile): boolean {
    if (!jobFile || !jobFile.containers || jobFile.containers.length === 0) {
      return false;
    }

    // Check all containers
    for (const container of jobFile.containers) {
      if (!container.services || container.services.length === 0) {
        continue;
      }

      // Check all location services in each container
      for (const locationService of container.services) {
        if (!locationService.services || locationService.services.length === 0) {
          continue;
        }

        // Check all services in each location service
        for (const service of locationService.services) {
          // Check if both plannedStartDateTime and plannedTimeToLeave exist and are valid dates
          if (!service.plannedStartDateTime || !service.plannedTimeToLeave) {
            return false;
          }

          // Validate that they are actual Date objects or valid date strings
          const startDate = new Date(service.plannedStartDateTime);
          const endDate = new Date(service.plannedTimeToLeave);

          if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
            return false;
          }
        }
      }
    }

    return true;
  }

  formatDocumentName(name: string): string {
    if (name === 'Consolidated Load Manifest') {
      return 'Consolidated<br>Load Manifest';
    }
    return name;
  }

  shouldShowInfoIcon(documentName: string): boolean {
    // Show info icon for Load Manifest and Consolidated Load Manifest when download is not available
    return documentName === 'Load Manifest' || documentName === 'Consolidated Load Manifest';
  }

  onDownload(documentName: string): void {
    if (!this.data?.orgId || !this.data?.jobRefId) {
      console.error('Missing orgId or jobRefId');
      return;
    }

    if (documentName === 'Consolidated Load Manifest') {
      this.loadManifestService.getConsolidatedLoadManifest(this.data.orgId, this.data.jobRefId)
        .pipe(
          tap((res: any) => {
            const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = 'CONSOLIDATED LOAD MANIFEST - ' + this.data.jobRefId + '.xlsx';
            a.click();
            a.remove();
          }),
          catchError((error) => {
            console.error('Error downloading Consolidated Load Manifest:', error);
            return of(null);
          })
        )
        .subscribe();
    } else if (documentName === 'Load Manifest') {
      this.loadManifestService.getLoadManifestsZip(this.data.orgId, this.data.jobRefId)
        .pipe(
          tap((res: any) => {
            const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = 'LOAD MANIFEST - ' + this.data.jobRefId + '.zip';
            a.click();
            a.remove();
          }),
          catchError((error) => {
            console.error('Error downloading Load Manifest:', error);
            return of(null);
          })
        )
        .subscribe();
    } else if (documentName === 'Packing List') {
      this.jobFileService.getAllPackingListsZip(this.data.orgId, this.data.jobRefId)
        .pipe(
          tap((blob: Blob) => {
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = 'PACKING LIST - ' + this.data.jobRefId + '.zip';
            a.click();
            a.remove();
          }),
          catchError((error) => {
            console.error('Error downloading Packing List:', error);
            return of(null);
          })
        )
        .subscribe();
    } else {
      console.log('Download:', documentName);
      // TODO: Implement download logic for other documents
    }
  }

  onGetArtifacts(): void {
    this.dialogRef.close();
  }

  onProceed(): void {
    this.dialogRef.close();
  }
}

